<template>
  <transition name="fp">
    <div v-if="open && np.id" class="fp" role="dialog" aria-label="Now playing" :style="bgVars">
      <div class="fp-bg" :style="bgStyle"></div>
      <div class="fp-veil"></div>

      <header class="fp-top">
        <button class="fp-icon" @click="$emit('close')" aria-label="Minimise player">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="fp-tabs" role="tablist" :style="{ visibility: wide ? 'hidden' : 'visible' }">
          <button role="tab" :aria-selected="view === 'art'" :class="{ on: view === 'art' }" @click="pick('art')">Now playing</button>
          <button role="tab" :aria-selected="view === 'lyrics'" :class="{ on: view === 'lyrics' }" @click="pick('lyrics')">Lyrics</button>
        </div>
        <div class="fp-right">
          <button class="fp-icon" @click="shareOpen = true" aria-label="Share a clip">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 15V4M12 4l-4 4M12 4l4 4M5 13v5a2 2 0 002 2h10a2 2 0 002-2v-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <button class="fp-icon" @click="$emit('toggle-comments')" aria-label="Comments">💬</button>
        </div>
      </header>

      <main class="fp-main" :class="{ lyr: view === 'lyrics' }">
        <!-- Artwork -->
        <section class="fp-art-col" v-show="view === 'art' || wide">
          <div class="fp-art" :class="{ playing: !np.paused }" :style="artStyle">{{ np.cover ? "" : "♪" }}</div>
        </section>

        <!-- Lyrics -->
        <section class="fp-lyrics-col" v-show="view === 'lyrics' || wide">
          <div v-if="loadingLyrics" class="fp-empty">Loading lyrics…</div>
          <div v-else-if="!parsed.lines.length" class="fp-empty">
            <div class="fp-empty-big">No lyrics yet</div>
            <div>The artist hasn't added lyrics for this song.</div>
          </div>
          <div v-else ref="scroller" class="fp-lines" :class="{ plain: !parsed.synced }" @wheel.passive="userScrolled" @touchmove.passive="userScrolled">
            <div class="fp-pad"></div>
            <template v-for="(l, i) in parsed.lines">
              <div v-if="!l.text" :key="'b' + i" class="fp-gap"></div>
              <div v-else :key="i" class="fp-line"
                :class="{ cur: parsed.synced && i === active, past: parsed.synced && i < active, seekable: parsed.synced }"
                :data-i="i" @click="seekTo(l)">{{ l.text }}</div>
            </template>
            <div class="fp-pad"></div>
          </div>
          <button v-if="parsed.synced && detached" class="fp-resync" @click="resync">Back to current line</button>
        </section>
      </main>

      <footer class="fp-foot">
        <div class="fp-meta">
          <div class="fp-title">{{ np.title }}</div>
          <div class="fp-artist">{{ np.artist }}</div>
        </div>
        <div class="fp-seek" @click="onSeek" role="slider" aria-label="Seek" :aria-valuenow="Math.round(state.currentTime)" :aria-valuemax="Math.round(state.duration)">
          <div class="fp-seek-bar"><div class="fp-seek-fill" :style="{ width: pct + '%' }"></div></div>
        </div>
        <div class="fp-times"><span>{{ fmt(state.currentTime) }}</span><span>-{{ fmt(Math.max(0, state.duration - state.currentTime)) }}</span></div>
        <div class="fp-ctl">
          <button class="fp-skip" @click="prev" aria-label="Previous">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h2v14H6zM20 5v14L9 12z"/></svg>
          </button>
          <button class="fp-play" @click="toggle" :aria-label="np.paused ? 'Play' : 'Pause'">
            <svg v-if="np.paused" width="30" height="30" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13a1 1 0 001.5.86l10.5-6.5a1 1 0 000-1.72L9.5 4.64A1 1 0 008 5.5z"/></svg>
            <svg v-else width="30" height="30" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
          </button>
          <button class="fp-skip" @click="next" aria-label="Next">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M16 5h2v14h-2zM4 5v14l11-7z"/></svg>
          </button>
        </div>
      </footer>
      <share-clip-modal v-if="shareOpen" :track-id="String(np.id)" :title="np.title" :duration="state.duration || 0" @close="shareOpen = false" />
    </div>
  </transition>
</template>

<script>
import playerStore from "@/services/playerStore";
import { coverBg } from "@/services/cover";
import { db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";
import { parseLyrics, activeIndex } from "@/services/lyrics";
import ShareClipModal from "@/components/ShareClipModal.vue";

const cache = {};   // trackId → lyrics text, so skipping around never re-reads

export default {
  name: "FullPlayer",
  components: { ShareClipModal },
  props: { open: { type: Boolean, default: false } },
  data() { return { shareOpen: false, view: "art", lyricsText: "", loadingLyrics: false, detached: false, wide: false }; },
  computed: {
    state() { return playerStore.state; },
    np() { return playerStore.state.nowPlaying; },
    pct() { return this.state.duration ? Math.min(100, (this.state.currentTime / this.state.duration) * 100) : 0; },
    parsed() { return parseLyrics(this.lyricsText); },
    active() { return this.parsed.synced ? activeIndex(this.parsed.lines, this.state.currentTime) : -1; },
    artStyle() { return coverBg(this.np.cover, this.np.color); },
    bgVars() { return { "--fp-c": this.np.color || "#7c5cff" }; },
    bgStyle() {
      return this.np.cover
        ? { backgroundImage: `url("${String(this.np.cover).replace(/"/g, "%22")}")` }
        : { background: `radial-gradient(120% 90% at 30% 10%, ${this.np.color || "#7c5cff"}, #0c0a0e 70%)` };
    },
  },
  watch: {
    "np.id": { immediate: true, handler(id) { this.loadLyrics(id); } },
    open(v) {
      if (v) { this._userPicked = false; this.view = this.parsed.lines.length ? "lyrics" : "art"; this.$nextTick(() => { this.measure(); this.centerActive(true); }); window.addEventListener("keydown", this.onKey); }
      else window.removeEventListener("keydown", this.onKey);
    },
    // When the lyrics arrive, open straight onto them if the song has any (the point of the full screen).
    parsed(p) { if (this.open && p.lines.length && this.view === "art" && !this._userPicked) this.view = "lyrics"; },
    active() { if (!this.detached) this.centerActive(false); },
    view() { this.$nextTick(() => this.centerActive(true)); },
  },
  mounted() { this.measure(); window.addEventListener("resize", this.measure); },
  beforeDestroy() { window.removeEventListener("resize", this.measure); window.removeEventListener("keydown", this.onKey); clearTimeout(this._dt); },
  methods: {
    pick(v) { this._userPicked = true; this.view = v; },
    measure() { this.wide = window.innerWidth >= 960; },
    async loadLyrics(id) {
      this.detached = false; this.lyricsText = "";
      if (!id) return;
      if (cache[id] !== undefined) { this.lyricsText = cache[id]; return; }
      this.loadingLyrics = true;
      try {
        const s = await getDoc(doc(db, "tracks", id));
        const text = (s.exists() && s.data().lyrics) || "";
        cache[id] = text;
        if (this.np.id === id) this.lyricsText = text;
      } catch (e) { /* offline or no access: show the empty state */ }
      if (this.np.id === id) this.loadingLyrics = false;
    },
    // The line being sung stays about a third of the way down, like Apple Music.
    centerActive(instant) {
      const box = this.$refs.scroller; if (!box || !this.parsed.synced) return;
      const el = box.querySelector(`[data-i="${Math.max(this.active, 0)}"]`); if (!el) return;
      const top = el.offsetTop - box.clientHeight * 0.34 + el.clientHeight / 2;
      this._auto = true;
      box.scrollTo({ top, behavior: instant ? "auto" : "smooth" });
      clearTimeout(this._dt); this._dt = setTimeout(() => { this._auto = false; }, 700);
    },
    // Scrolling by hand pauses the auto-follow until "Back to current line".
    userScrolled() { if (!this._auto && this.parsed.synced) this.detached = true; },
    resync() { this.detached = false; this.centerActive(false); },
    seekTo(l) {
      if (!this.parsed.synced || l.t == null || !this.state.duration) return;
      playerStore.seekToPct(l.t / this.state.duration);
      this.detached = false;
      if (this.np.paused) playerStore.toggle();
    },
    onKey(e) {
      if (e.key === "Escape") this.$emit("close");
      else if (e.key === " " && !/INPUT|TEXTAREA/.test((e.target && e.target.tagName) || "")) { e.preventDefault(); this.toggle(); }
    },
    toggle() { playerStore.toggle(); },
    next() { playerStore.playNext(); },
    prev() { playerStore.playPrevious(); },
    onSeek(e) { const r = e.currentTarget.getBoundingClientRect(); playerStore.seekToPct((e.clientX - r.left) / r.width); },
    fmt(s) { s = Math.floor(s || 0); return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0"); },
  },
};
</script>

<style scoped>
.fp{position:fixed;inset:0;z-index:10000;color:#fff;display:flex;flex-direction:column;overflow:hidden;font-family:'DM Sans','Helvetica Neue',sans-serif;background:#0c0a0e}
.fp-bg{position:absolute;inset:-60px;background-size:cover;background-position:center;filter:blur(60px) saturate(1.4);transform:scale(1.15);opacity:.85}
.fp-veil{position:absolute;inset:0;background:linear-gradient(180deg,rgba(12,10,14,.35) 0%,rgba(12,10,14,.55) 55%,rgba(12,10,14,.9) 100%)}
.fp > *:not(.fp-bg):not(.fp-veil){position:relative}
.fp-top{display:flex;align-items:center;justify-content:space-between;padding:14px 18px}
.fp-icon{background:rgba(255,255,255,.1);border:none;color:#fff;width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:16px;-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px)}
.fp-right{display:flex;gap:8px}
.fp-icon:hover{background:rgba(255,255,255,.18)}
.fp-tabs{display:flex;background:rgba(255,255,255,.1);border-radius:999px;padding:3px;-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px)}
.fp-tabs button{background:none;border:none;color:rgba(255,255,255,.65);font-family:inherit;font-size:13px;font-weight:600;padding:7px 16px;border-radius:999px;cursor:pointer}
.fp-tabs button.on{background:rgba(255,255,255,.95);color:#15121a}
.fp-main{flex:1;min-height:0;display:flex;align-items:center;justify-content:center;gap:56px;padding:0 24px}
.fp-art-col{display:flex;align-items:center;justify-content:center;flex:0 1 auto}
.fp-art{width:min(78vw,46vh,420px);aspect-ratio:1/1;border-radius:18px;box-shadow:0 30px 70px -10px rgba(0,0,0,.65);display:flex;align-items:center;justify-content:center;font-size:90px;color:rgba(255,255,255,.5);transition:transform .45s cubic-bezier(.2,.8,.2,1)}
.fp-art:not(.playing){transform:scale(.9)}
.fp-lyrics-col{position:relative;flex:1 1 0;min-width:0;max-width:640px;height:100%;display:flex;flex-direction:column}
.fp-lines{position:relative;flex:1;overflow-y:auto;scrollbar-width:none;-webkit-mask-image:linear-gradient(180deg,transparent 0,#000 12%,#000 80%,transparent 100%);mask-image:linear-gradient(180deg,transparent 0,#000 12%,#000 80%,transparent 100%)}
.fp-lines::-webkit-scrollbar{display:none}
.fp-pad{height:34vh}
.fp-line{font-size:clamp(24px,3.4vw,38px);line-height:1.22;font-weight:800;letter-spacing:-.01em;padding:9px 10px;margin:0 -10px;border-radius:12px;color:rgba(255,255,255,.38);transition:color .35s,transform .35s,opacity .35s;transform-origin:left center}
.fp-line.seekable{cursor:pointer}
.fp-line.seekable:hover{color:rgba(255,255,255,.7)}
.fp-line.cur{color:#fff;transform:scale(1.02)}
.fp-line.past{color:rgba(255,255,255,.3)}
.fp-lines.plain .fp-line{color:rgba(255,255,255,.92);font-weight:600;font-size:clamp(18px,2.4vw,26px);line-height:1.45;padding:3px 10px}
.fp-lines.plain .fp-pad{height:6vh}
.fp-gap{height:22px}
.fp-empty{margin:auto;text-align:center;color:rgba(255,255,255,.6);font-size:15px}
.fp-empty-big{font-size:26px;font-weight:800;color:rgba(255,255,255,.85);margin-bottom:6px}
.fp-resync{position:absolute;bottom:10px;left:50%;transform:translateX(-50%);background:rgba(255,255,255,.95);color:#15121a;border:none;border-radius:999px;padding:9px 18px;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 8px 24px rgba(0,0,0,.4)}
.fp-foot{padding:6px 28px 30px;max-width:720px;width:100%;margin:0 auto}
.fp-meta{margin-bottom:12px}
.fp-title{font-size:20px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fp-artist{font-size:15px;color:rgba(255,255,255,.6);margin-top:2px}
.fp-seek{padding:9px 0;cursor:pointer}
.fp-seek-bar{height:5px;border-radius:3px;background:rgba(255,255,255,.22);overflow:hidden}
.fp-seek-fill{height:100%;background:#fff;border-radius:3px}
.fp-times{display:flex;justify-content:space-between;font-size:11.5px;color:rgba(255,255,255,.55);font-variant-numeric:tabular-nums;margin-top:2px}
.fp-ctl{display:flex;align-items:center;justify-content:center;gap:34px;margin-top:10px}
.fp-skip{background:none;border:none;color:#fff;cursor:pointer;padding:8px;opacity:.9}
.fp-skip:hover{opacity:1}
.fp-play{width:68px;height:68px;border-radius:50%;border:none;background:#fff;color:#15121a;display:flex;align-items:center;justify-content:center;cursor:pointer}
.fp-play svg{margin-left:2px}
.fp-enter-active,.fp-leave-active{transition:transform .38s cubic-bezier(.2,.8,.2,1),opacity .25s}
.fp-enter,.fp-leave-to{transform:translateY(100%);opacity:.6}
@media (prefers-reduced-motion:reduce){.fp-enter-active,.fp-leave-active,.fp-line,.fp-art{transition:none}}
/* Narrow screens: one view at a time (Now playing or Lyrics). */
@media (max-width:959px){
  .fp-main{gap:0;padding:0 18px}
  .fp-lyrics-col{max-width:none}
  .fp-foot{padding:6px 22px 22px}
  .fp-line{font-size:clamp(24px,7vw,32px)}
}
</style>